/**
 * Markdown 轉 HTML 工具函數
 * 統一管理所有 Markdown 到 HTML 的轉換邏輯
 */

/**
 * 確保表格完整性 - 檢查並修復不完整的表格
 * @param {string} text - Markdown 文本
 * @returns {string} - 修復後的文本
 */
const ensureTableCompleteness = (text) => {
  if (!text) return text;
  
  // 檢查是否有未完成的表格（有開始的 | 但沒有正確的結束）
  const lines = text.split('\n');
  let inTable = false;
  let tableStartIndex = -1;
  let lastTableLineIndex = -1;
  let columnCount = 0;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // 檢測表格開始（行開始和結束都有 |）
    if (line.startsWith('|') && line.endsWith('|')) {
      if (!inTable) {
        inTable = true;
        tableStartIndex = i;
        // 計算列數（排除開頭和結尾的空字符串）
        columnCount = line.split('|').filter(cell => cell.trim() !== '').length;
      }
      lastTableLineIndex = i;
      
      // 檢查當前行的列數
      const currentColumnCount = line.split('|').filter(cell => cell.trim() !== '').length;
      
      // 如果列數不匹配且這是表格的最後一行（在文本末尾），補齊缺少的單元格
      if (currentColumnCount < columnCount && i === lines.length - 1) {
        const missingColumns = columnCount - currentColumnCount;
        let fixedLine = line.endsWith('|') ? line.slice(0, -1) : line;
        for (let j = 0; j < missingColumns; j++) {
          fixedLine += ' | ';
        }
        fixedLine += ' |';
        lines[i] = fixedLine;
      }
    } else if (inTable && line && !line.startsWith('|')) {
      // 表格結束
      inTable = false;
      columnCount = 0;
    }
  }
  
  // 如果在文本結尾仍在表格中，且最後一行不是完整的表格行
  if (inTable && lastTableLineIndex >= 0) {
    const lastLine = lines[lastTableLineIndex].trim();
    
    // 檢查最後一行是否是完整的表格行（開始和結束都有 |）
    if (lastLine.startsWith('|') && !lastLine.endsWith('|')) {
      // 補上缺少的結束符
      lines[lastTableLineIndex] = lastLine + ' |';
    }
    
    // 如果最後一行不以 | 開始，可能整行都不完整，移除它
    if (!lastLine.startsWith('|') && lastLine.length > 0) {
      // 這可能是表格外的文本，保持原樣
    }
  }
  
  return lines.join('\n');
};

/**
 * 將 Markdown 轉為 HTML（資訊主頁專用樣式）
 * 樣式規範：H1 黑色、H2 藍色、H3 綠色、有序列表有底色
 * @param {string} markdownText - Markdown 文本
 * @returns {string} - 轉換後的 HTML
 */
export const convertMarkdownToHtml = (markdownText) => {
  if (!markdownText) return '';
  
  // 先確保表格完整性
  let html = ensureTableCompleteness(markdownText);
  
  // 表格轉換 - 支持多列數
  const tableRegex = /(\|[^\n]+\|\n\|[\s\-:|]+\|\n(\|[^\n]+\|\n?)+)/g;
  html = html.replace(tableRegex, (match) => {
    const lines = match.trim().split('\n');
    if (lines.length < 3) return match; // 至少需要表頭、分隔行和一行數據
    
    let tableHtml = '<table style="border-collapse: collapse; width: auto; max-width: 100%; margin: 10px 0; border: 1px solid #ddd; font-size: 14px; table-layout: auto; word-wrap: break-word;">';
    
    lines.forEach((line, index) => {
      if (index === 1) return; // 跳過分隔行
      
      const cells = line.split('|').filter(cell => cell.trim() !== '');
      if (cells.length === 0) return;
      
      const tag = index === 0 ? 'th' : 'td';
      tableHtml += '<tr>';
      
      cells.forEach((cell, cellIndex) => {
        const content = cell.trim();
        
        let style = '';
        if (index === 0) {
          // 表頭樣式 - 綠底白字，全部左對齊
          style = 'background-color: #57A300; color: white; font-weight: bold; padding: 10px 12px; border: 1px solid #ddd; text-align: left; font-size: 14px; white-space: nowrap; overflow-wrap: normal;';
        } else {
          // 數據行樣式 - 全部左對齊
          style = 'padding: 10px 12px; border: 1px solid #ddd; text-align: left; font-size: 14px; color: #333; white-space: normal; overflow-wrap: break-word; word-wrap: break-word;';
        }
        
        tableHtml += `<${tag} style="${style}">${content}</${tag}>`;
      });
      
      tableHtml += '</tr>';
    });
    tableHtml += '</table>';
    return tableHtml;
  });
  
  // 粗體、斜體
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>');
  html = html.replace(/__(.*?)__/g, '<strong style="font-weight: 600;">$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.*?)_/g, '<em>$1</em>');
  
  // 標題 - H1 黑色、H2 藍色、H3 綠色
  html = html.replace(/^#### (.*$)/gim, '<h4 style="margin: 12px 0 6px 0; color: #4a5568; font-size: 16px; font-weight: 600; max-width: 100%; word-wrap: break-word;">$1</h4>');
  html = html.replace(/^### (.*$)/gim, '<h3 style="margin: 16px 0 8px 0; color: #228B22; font-size: 18px; font-weight: 600; max-width: 100%; word-wrap: break-word;">$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2 style="margin: 20px 0 12px 0; color: #1890ff; font-size: 20px; font-weight: 600; max-width: 100%; word-wrap: break-word;">$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1 style="margin: 24px 0 16px 0; color: #000000; font-size: 24px; font-weight: 700; max-width: 100%; word-wrap: break-word;">$1</h1>');
  
  // Markdown 有序列表 (1. 2. 3.) - 有底色
  const olLines = html.split('\n');
  const olResult = [];
  let olBuffer = [];
  const flushOl = () => {
    if (olBuffer.length > 0) {
      const items = olBuffer.map((item) =>
        `<li style="line-height: 1.9; color: #1a1a1a; padding: 8px 12px; margin: 8px 0; background-color: #e8f4f8; border-left: 3px solid #1890ff; border-radius: 3px;">${item}</li>`
      ).join('');
      olResult.push(`<ol style="margin: 12px 0 12px 24px; padding: 16px 24px; background-color: #e6f7ff; border-radius: 6px; list-style-position: inside;">${items}</ol>`);
      olBuffer = [];
    }
  };
  for (const line of olLines) {
    const m = line.match(/^(\d+)\.\s+(.*)$/);
    if (m) {
      olBuffer.push(m[2]);
    } else {
      flushOl();
      olResult.push(line);
    }
  }
  flushOl();
  html = olResult.join('\n');
  
  // 無序列表（簡化版）- 突出显示，粗体由 ** 控制
  html = html.replace(/^\* (.*$)/gim, '<li style="margin: 10px 0; line-height: 1.9; color: #1a1a1a; padding-left: 8px; border-left: 3px solid #52c41a; background-color: #f6ffed; padding: 8px 8px 8px 12px; border-radius: 3px; max-width: 100%; word-wrap: break-word; box-sizing: border-box;">$1</li>');
  html = html.replace(/^- (.*$)/gim, '<li style="margin: 10px 0; line-height: 1.9; color: #1a1a1a; padding-left: 8px; border-left: 3px solid #52c41a; background-color: #f6ffed; padding: 8px 8px 8px 12px; border-radius: 3px; max-width: 100%; word-wrap: break-word; box-sizing: border-box;">$1</li>');
  
  // 若 AI 已輸出 HTML 列表，統一調整樣式
  html = html.replace(/<ul([^>]*)>/g, (_m, attrs) => {
    const hasStyle = /style="/i.test(attrs || '');
    if (hasStyle) {
      return `<ul${attrs.replace(/style="([^"]*)"/i, (_m2, st) => `style="${st}; margin: 12px 0 12px 40px; padding-left: 0; list-style: none;"`)}>`;
    }
    return `<ul style="margin: 12px 0 12px 40px; padding-left: 0; list-style: none;">`;
  });
  
  // 有序列表 HTML 標籤 - 添加底色樣式
  html = html.replace(/<ol([^>]*)>/g, (_m, attrs) => {
    const hasStyle = /style="/i.test(attrs || '');
    const baseStyle = 'margin: 12px 0 12px 40px; padding: 16px 24px; background-color: #e6f7ff; border-radius: 6px; list-style-position: inside;';
    if (hasStyle) {
      return `<ol${attrs.replace(/style="([^"]*)"/i, (_m2, st) => `style="${st}; ${baseStyle}"`)}>`;
    }
    return `<ol style="${baseStyle}">`;
  });
  
  // 为 ol > li 添加特殊样式（有序列表項目底色）
  html = html.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/g, (match) => {
    return match.replace(/<li([^>]*)>/g, (liMatch) => {
      const hasStyle = /style="/i.test(liMatch);
      if (hasStyle) {
        return liMatch.replace(/style="([^"]*)"/i, (_, st) =>
          `style="${st}; line-height: 1.9; color: #1a1a1a; padding: 8px 12px; margin: 8px 0; background-color: #e8f4f8; border-left: 3px solid #1890ff; border-radius: 3px;"`);
      }
      return `<li style="line-height: 1.9; color: #1a1a1a; padding: 8px 12px; margin: 8px 0; background-color: #e8f4f8; border-left: 3px solid #1890ff; border-radius: 3px;">`;
    });
  });
  
  // 段落 - 添加左侧缩进以凸显标题
  html = html.replace(/\n\n/g, '</p><p style="margin: 10px 0 10px 20px; line-height: 1.9; color: #333; text-align: justify; max-width: 100%; word-wrap: break-word;">');
  html = html.replace(/^(?!<[h|t|u|o|li])(.*$)/gim, '<p style="margin: 10px 0 10px 20px; line-height: 1.9; color: #333; text-align: justify; max-width: 100%; word-wrap: break-word;">$1</p>');
  html = html.replace(/<p><\/p>/g, '');
  html = html.replace(/<p style="margin: 10px 0 10px 20px; line-height: 1.9; color: #333; text-align: justify; max-width: 100%; word-wrap: break-word;"><\/p>/g, '');
  
  // 处理可能存在的代码块或预格式化文本
  html = html.replace(/<pre([^>]*)>/g, '<pre$1 style="max-width: 100%; overflow-x: auto; white-space: pre-wrap; word-wrap: break-word;">');
  html = html.replace(/<code([^>]*)>/g, '<code$1 style="max-width: 100%; word-wrap: break-word;">');
  
  // 包装整个内容到一个容器中，确保不会超出宽度
  html = `<div style="width: 100%; max-width: 100%; overflow-x: hidden; box-sizing: border-box; overflow-wrap: break-word; word-wrap: break-word; word-break: break-word;">${html}</div>`;
  
  return html;
};

/**
 * 將 AI 串流文本以 HTML 呈現（參考 App_transcript.js）
 * 1) 過濾 <think> 區塊
 * 2) 轉換 Markdown 為 HTML
 * @param {string} text - AI 輸出的文本
 * @returns {string} - 處理後的 HTML
 */
export const formatAIContentToHtml = (text) => {
  try {
    if (!text) return '';
    // 過濾 <think> 內容
    let s = text
      .replace(/<think>.*?<\/think>/gs, '')
      .replace(/<think>.*$/gs, '')
      .replace(/^.*?<\/think>/gs, '')
      .replace(/<think>/g, '')
      .replace(/<\/think>/g, '');
    // 一律執行 Markdown -> HTML 轉換
    return convertMarkdownToHtml(s);
  } catch (_) {
    return '';
  }
};

export default {
  convertMarkdownToHtml,
  formatAIContentToHtml
};
